import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Portal, Box, Typography } from '@mui/material';
import HotelIcon from '@mui/icons-material/Hotel';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import PlaceIcon from '@mui/icons-material/Place';
import searchNormalIcon from '../../../assets/search-normal.svg';
import { HOTEL_SEARCH_ICON_COLOR } from '../../../constants/hotelSearch.constants';
import { getHotelAutocompleteSectionOrder } from '../../../utils/hotelAutocompleteResults';
import { sliceAutocompleteSection } from '../../../utils/hotelAutocompleteSection';
import HotelAutocompleteLoadMore from '../../Hotels/HotelAutocompleteLoadMore/HotelAutocompleteLoadMore';
import HotelLocationTypeIcon from '../../Hotels/HotelLocationTypeIcon/HotelLocationTypeIcon';
import HotelFuzzyMatchName from '../../Hotels/HotelFuzzyMatchName/HotelFuzzyMatchName';
import HotelAutocompleteLabel from '../../Hotels/HotelAutocompleteLabel/HotelAutocompleteLabel';
import HotelAutocompleteStars from '../../Hotels/HotelAutocompleteStars/HotelAutocompleteStars';
import HotelNearbyLocationOption from '../../Hotels/HotelNearbyLocationOption/HotelNearbyLocationOption';
import {
  createGtmAttributes,
  GTM_CLICK_IDS,
  GTM_CONTEXT_ATTRS,
} from '../../../analytics/gtmClickIds';

/**
 * Hotels-only bottom sheet for destination search — shell and list patterns match Flight mobile airport sheet
 * (FlightForm.css global classes for search bar / rows); logic stays in HotelForm.
 */
function HotelMobileDestinationSheet({
  open,
  onClose,
  locationInput,
  onLocationInputChange,
  onSearch,
  onOpenSearch,
  locationLoading,
  cityResults,
  hotelResults,
  locationResults = [],
  queryType,
  onSelectCity,
  onSelectHotel,
  onSelectLocation,
  validationError,
  onNearbyError,
  nearbyGtmContext = 'home-mobile',
}) {
  const inputRef = useRef(null);
  // The click that opens this sheet is delivered to the backdrop in the same turn
  // the portal mounts. Ignore close until that gesture has finished.
  const blockCloseRef = useRef(false);
  const wasOpenRef = useRef(false);
  if (open && !wasOpenRef.current) {
    blockCloseRef.current = true;
  }
  if (!open) {
    blockCloseRef.current = false;
  }
  wasOpenRef.current = open;

  useLayoutEffect(() => {
    if (!open) return undefined;
    const id = window.setTimeout(() => {
      blockCloseRef.current = false;
    }, 0);
    return () => window.clearTimeout(id);
  }, [open]);

  const requestClose = (event) => {
    if (event && typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }
    if (blockCloseRef.current) return;
    onClose();
  };

  // Body scroll lock — same strategy as TravellerDetails useMenuScrollLock (position:fixed + restore scrollY).
  useEffect(() => {
    if (!open) return;

    const scrollY = window.scrollY;
    const { body } = document;
    const previousStyles = {
      position: { value: body.style.getPropertyValue('position'), priority: body.style.getPropertyPriority('position') },
      top: { value: body.style.getPropertyValue('top'), priority: body.style.getPropertyPriority('top') },
      left: { value: body.style.getPropertyValue('left'), priority: body.style.getPropertyPriority('left') },
      right: { value: body.style.getPropertyValue('right'), priority: body.style.getPropertyPriority('right') },
      width: { value: body.style.getPropertyValue('width'), priority: body.style.getPropertyPriority('width') },
      overflow: { value: body.style.getPropertyValue('overflow'), priority: body.style.getPropertyPriority('overflow') },
    };

    body.style.setProperty('position', 'fixed', 'important');
    body.style.setProperty('top', `-${scrollY}px`, 'important');
    body.style.setProperty('left', '0', 'important');
    body.style.setProperty('right', '0', 'important');
    body.style.setProperty('width', '100%', 'important');
    body.style.setProperty('overflow', 'hidden', 'important');

    return () => {
      Object.entries(previousStyles).forEach(([prop, { value, priority }]) => {
        if (priority) {
          body.style.setProperty(prop, value, priority);
        } else {
          body.style.setProperty(prop, value);
        }
      });
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = requestAnimationFrame(() => {
      inputRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(t);
  }, [open]);

  const [expandedAutocomplete, setExpandedAutocomplete] = useState({
    city: false,
    hotel: false,
    location: false,
  });

  useEffect(() => {
    setExpandedAutocomplete({ city: false, hotel: false, location: false });
  }, [cityResults, hotelResults, locationResults]);

  if (!open) return null;

  const hasResults = cityResults.length > 0 || hotelResults.length > 0 || locationResults.length > 0;
  const citySection = sliceAutocompleteSection(cityResults, expandedAutocomplete.city);
  const hotelSection = sliceAutocompleteSection(hotelResults, expandedAutocomplete.hotel);
  const locationSection = sliceAutocompleteSection(locationResults, expandedAutocomplete.location);
  const expandSection = (section) => {
    setExpandedAutocomplete((prev) => ({ ...prev, [section]: true }));
  };

  const renderCities = () =>
    cityResults.length > 0 && (
      <>
        <Typography
          sx={{
            px: '14px',
            pt: '10px',
            pb: '6px',
            fontSize: '11px',
            fontWeight: 700,
            color: '#9ca3af',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            borderTop: queryType === 'hotel' && hotelResults.length > 0 ? '1px solid #f0f0f0' : 'none',
          }}
        >
          Cities & Destinations
        </Typography>
        {citySection.visible.map((loc, index) => (
          <Box
            key={`city-${loc.cityId || index}`}
            role="button"
            tabIndex={0}
            className="mobile-sheet-option"
            onClick={() => onSelectCity(loc)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelectCity(loc);
              }
            }}
            {...createGtmAttributes(GTM_CLICK_IDS.HOTEL_SEARCH_AUTOCOMPLETE_SELECT, {
              [GTM_CONTEXT_ATTRS.CONTEXT]: 'city',
            })}
          >
            <Box sx={{ width: '18px', height: '18px', mr: '12px', flexShrink: 0, mt: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LocationCityIcon sx={{ fontSize: '18px', color: HOTEL_SEARCH_ICON_COLOR.CITY }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography className="flight-dropdown-option-city" sx={{ fontSize: '15px !important' }}>
                    <HotelAutocompleteLabel label={loc.cityName} />
                  </Typography>
                  <Typography className="flight-dropdown-option-airport">
                    {loc.countryName}
                  </Typography>
                </Box>
                {loc.countryCode && (
                  <Typography sx={{ fontFamily: 'Host Grotesk', fontSize: '13px', fontWeight: 700, color: '#545665', flexShrink: 0, ml: '8px' }}>
                    {loc.countryCode}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        ))}
        <HotelAutocompleteLoadMore
          visible={citySection.showLoadMore}
          onLoadMore={() => expandSection('city')}
        />
      </>
    );

  const renderHotels = () =>
    hotelResults.length > 0 && (
      <>
        <Typography
          sx={{
            px: '14px',
            pt: queryType === 'city' && cityResults.length > 0 ? '8px' : '10px',
            pb: '6px',
            fontSize: '11px',
            fontWeight: 700,
            color: '#9ca3af',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            borderTop: queryType === 'city' && cityResults.length > 0 ? '1px solid #f0f0f0' : 'none',
          }}
        >
          Hotels
        </Typography>
        {hotelSection.visible.map((loc, index) => (
          <Box
            key={`hotel-${loc.hotelCode || index}`}
            role="button"
            tabIndex={0}
            className="mobile-sheet-option"
            onClick={() => onSelectHotel(loc)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelectHotel(loc);
              }
            }}
            {...createGtmAttributes(GTM_CLICK_IDS.HOTEL_SEARCH_AUTOCOMPLETE_SELECT, {
              [GTM_CONTEXT_ATTRS.CONTEXT]: 'hotel',
            })}
          >
            <Box sx={{ width: '18px', height: '18px', mr: '12px', flexShrink: 0, mt: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <HotelIcon sx={{ fontSize: '16px', color: HOTEL_SEARCH_ICON_COLOR.HOTEL }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography component="div" className="flight-dropdown-option-city" sx={{ fontSize: '15px !important' }}>
                <HotelFuzzyMatchName name={loc.hotelName} />
              </Typography>
              <Typography className="flight-dropdown-option-airport">
                <HotelAutocompleteLabel label={[loc.cityName, loc.countryName].filter(Boolean).join(', ')} />
                <HotelAutocompleteStars value={loc.starRating} />
              </Typography>
            </Box>
          </Box>
        ))}
        <HotelAutocompleteLoadMore
          visible={hotelSection.showLoadMore}
          onLoadMore={() => expandSection('hotel')}
        />
      </>
    );

  const renderLocations = () =>
    locationResults.length > 0 && (
      <>
        <Typography
          sx={{
            px: '14px',
            pt: '8px',
            pb: '6px',
            fontSize: '11px',
            fontWeight: 700,
            color: '#9ca3af',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            borderTop: (cityResults.length > 0 || hotelResults.length > 0) ? '1px solid #f0f0f0' : 'none',
          }}
        >
          Locations
        </Typography>
        {locationSection.visible.map((loc, index) => (
          <Box
            key={`loc-${loc.locationId || index}`}
            role="button"
            tabIndex={0}
            className="mobile-sheet-option"
            onClick={() => onSelectLocation?.(loc)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelectLocation?.(loc);
              }
            }}
            {...createGtmAttributes(GTM_CLICK_IDS.HOTEL_SEARCH_AUTOCOMPLETE_SELECT, {
              [GTM_CONTEXT_ATTRS.CONTEXT]: 'location',
            })}
          >
            <Box sx={{ width: '18px', height: '18px', mr: '12px', flexShrink: 0, mt: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PlaceIcon sx={{ fontSize: '18px', color: HOTEL_SEARCH_ICON_COLOR.LOCATION }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Typography className="flight-dropdown-option-city" sx={{ fontSize: '15px !important', flex: 1, minWidth: 0 }}>
                  <HotelAutocompleteLabel label={loc.displayName} />
                </Typography>
                <HotelLocationTypeIcon locationType={loc.locationType} />
              </Box>
              <Typography className="flight-dropdown-option-airport">
                <HotelAutocompleteLabel label={[loc.cityName, loc.countryName].filter(Boolean).join(', ')} />
              </Typography>
            </Box>
          </Box>
        ))}
        <HotelAutocompleteLoadMore
          visible={locationSection.showLoadMore}
          onLoadMore={() => expandSection('location')}
        />
      </>
    );

  return (
    <Portal>
      <Box className={`hotel-mobile-destination-backdrop${open ? ' open' : ''}`} onClick={requestClose} aria-hidden />
      <Box className={`hotel-mobile-destination-sheet${open ? ' open' : ''}`} data-hotel-destination-sheet role="dialog" aria-modal="true">
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: '10px', pb: '2px' }}>
          <Box sx={{ width: '36px', height: '4px', borderRadius: '2px', background: '#D0D0D8' }} />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', px: '20px', pt: '10px', pb: '14px' }}>
          <Typography sx={{ flex: 1, fontFamily: 'Host Grotesk', fontSize: '18px', fontWeight: 700, color: '#20202A', lineHeight: '26px' }}>
            Search destination
          </Typography>
          <Box
            onClick={requestClose}
            sx={{ p: '6px', cursor: 'pointer', WebkitTapHighlightColor: 'transparent', ml: '8px' }}
            role="button"
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="#20202A" strokeWidth="2.2" strokeLinecap="round" />
            </svg>
          </Box>
        </Box>

        <Box sx={{ px: '16px', pb: '12px' }}>
          <div className="flight-dropdown-search-bar">
            <img src={searchNormalIcon} alt="" style={{ width: '24px', height: '24px', flexShrink: 0 }} />
            <input
              ref={inputRef}
              type="text"
              data-hotel-destination-input
              value={locationInput}
              placeholder="Search city, hotel, area or landmark"
              className="hotel-dropdown-search-input"
              autoComplete="off"
              onChange={(e) => onLocationInputChange(e.target.value)}
              onFocus={onOpenSearch}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onSearch?.();
                }
              }}
            />
          </div>
          {validationError && (
            <Typography sx={{ color: '#d32f2f', fontSize: '12px', fontFamily: 'Host Grotesk', mt: 1 }}>
              {validationError}
            </Typography>
          )}
        </Box>

        <HotelNearbyLocationOption
          variant="mobile"
          gtmContext={nearbyGtmContext}
          onSelect={onSelectLocation}
          onError={onNearbyError}
        />

        <Box className="mobile-sheet-results">
          {locationLoading && (
            <Box sx={{ px: '20px', py: '16px', color: '#7E808F', fontFamily: 'Host Grotesk', fontSize: '14px' }}>
              Searching places...
            </Box>
          )}
          {!locationLoading && !hasResults && (
            <Box sx={{ px: '20px', py: '16px', color: '#7E808F', fontFamily: 'Host Grotesk', fontSize: '14px' }}>
              Type at least 2 characters or browse popular destinations.
            </Box>
          )}
          {!locationLoading && hasResults && getHotelAutocompleteSectionOrder(queryType).map((section) => {
            if (section === 'city') return <React.Fragment key={section}>{renderCities()}</React.Fragment>;
            if (section === 'hotel') return <React.Fragment key={section}>{renderHotels()}</React.Fragment>;
            return <React.Fragment key={section}>{renderLocations()}</React.Fragment>;
          })}
        </Box>
      </Box>
    </Portal>
  );
}

export default React.memo(HotelMobileDestinationSheet);
